import React from "react";
import { IDDateRangeOption, TotalRow } from "../../InsightsDashboard";
import {
  formatIDNumber,
  formatIDPercent,
  renderComparisonValue,
} from "../../utils";
import Pictogram, {
  PictogramVariants,
} from "src/app/components/Pictogram/Pictogram";

import "./InsightsDashboardSummary.scss";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";

export type SummaryMetricKey =
  | "posts_count"
  | "unique_influencers"
  | "avg_er"
  | "sum_likes"
  | "avg_likes"
  | "sum_engagements"
  | "avg_engagements"
  | "sum_comments"
  | "avg_comments"
  | "avg_sentiment_positive"
  | "avg_sentiment_neutral"
  | "avg_sentiment_negative";

interface MetricConfig {
  key: SummaryMetricKey;
  pictogram: string;
  pictogramVariant: PictogramVariants;
  labelId: string;
  labelDefaultMessage: string;
  format: "number" | "percent";
}

const METRICS: MetricConfig[] = [
  {
    key: "posts_count",
    pictogram: "Social/publications",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_publications",
    labelDefaultMessage: "Publications",
    format: "number",
  },
  {
    key: "unique_influencers",
    pictogram: "Social/creators",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_creators",
    labelDefaultMessage: "Creators",
    format: "number",
  },
  {
    key: "sum_likes",
    pictogram: "Social/likes",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_likes",
    labelDefaultMessage: "Likes",
    format: "number",
  },
  {
    key: "avg_likes",
    pictogram: "Social/avgLikes",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_avg_likes",
    labelDefaultMessage: "Avg Likes",
    format: "number",
  },
  {
    key: "sum_engagements",
    pictogram: "Social/impressions",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_engagements",
    labelDefaultMessage: "Engagements",
    format: "number",
  },
  {
    key: "avg_engagements",
    pictogram: "Social/avgImpressions",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_avg_engagements",
    labelDefaultMessage: "Avg Engagements",
    format: "number",
  },
  {
    key: "avg_er",
    pictogram: "Social/avgEr",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_avg_er",
    labelDefaultMessage: "Avg ER",
    format: "percent",
  },
  {
    key: "sum_comments",
    pictogram: "Social/comments",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_comments",
    labelDefaultMessage: "Comments",
    format: "number",
  },
  {
    key: "avg_comments",
    pictogram: "Social/avgComments",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_avg_comments",
    labelDefaultMessage: "Avg Comments",
    format: "number",
  },
  {
    key: "avg_sentiment_positive",
    pictogram: "Sentiment/positive",
    pictogramVariant: PictogramVariants.ForestCrayola,
    labelId: "ws_positive_sentiment",
    labelDefaultMessage: "Positive sentiment",
    format: "percent",
  },
  {
    key: "avg_sentiment_neutral",
    pictogram: "Sentiment/neutral",
    pictogramVariant: PictogramVariants.SlateGrayAlt,
    labelId: "ws_neutral_sentiment",
    labelDefaultMessage: "Neutral sentiment",
    format: "percent",
  },
  {
    key: "avg_sentiment_negative",
    pictogram: "Sentiment/negative",
    pictogramVariant: PictogramVariants.Vermilion,
    labelId: "ws_negative_sentiment",
    labelDefaultMessage: "Negative sentiment",
    format: "percent",
  },
];

export const ALL_SUMMARY_METRICS: SummaryMetricKey[] = METRICS.map(
  (metric) => metric.key,
);

interface Props {
  data: TotalRow | null;
  comparisonData?: TotalRow | null;
  selectedDateRange: IDDateRangeOption | null;
  metrics?: SummaryMetricKey[];
  actions?: React.ReactNode;
  columns?: number;
}

export const InsightsDashboardSummary = (props: Props) => {
  const {
    data,
    comparisonData,
    selectedDateRange,
    metrics = ALL_SUMMARY_METRICS,
    actions,
    columns,
  } = props;

  const { showTrends } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );

  const renderComparisonValueWrapper = (value?: number | null) => {
    if (!showTrends) return null;

    return renderComparisonValue(value, selectedDateRange?.label);
  };

  if (!data) return null;

  const visibleMetrics = metrics
    .map((key) => METRICS.find((metric) => metric.key === key))
    .filter((metric): metric is MetricConfig => Boolean(metric));

  return (
    <section className="insights-dashboard-summary">
      <div className="insights-dashboard-summary__header">
        <span className="insights-dashboard-summary__title">
          <IDHFormattedMessage id="ws_summary" defaultMessage="Summary" />
        </span>

        {actions}
      </div>

      <div
        className="insights-dashboard-summary__content"
        style={
          columns
            ? ({ "--summary-columns": columns } as React.CSSProperties)
            : undefined
        }
      >
        {visibleMetrics.map((metric) => (
          <div className="insights-dashboard-summary__item" key={metric.key}>
            <span className="insights-dashboard-summary__item-label">
              <Pictogram
                identifier={metric.pictogram}
                variant={metric.pictogramVariant}
              />
              <IDHFormattedMessage
                id={metric.labelId}
                defaultMessage={metric.labelDefaultMessage}
              />
            </span>

            <span className="insights-dashboard-summary__item-value">
              {metric.format === "percent"
                ? formatIDPercent(data[metric.key])
                : formatIDNumber(data[metric.key])}
            </span>
            {renderComparisonValueWrapper(comparisonData?.[metric.key])}
          </div>
        ))}
      </div>
    </section>
  );
};
